import React, { createContext, useContext, useEffect, useState } from 'react';
import { paymentTranslations, PaymentMessage } from './paymentTranslations';

export type PaymentLanguage = 'ru' | 'en' | 'kk' | 'ky';
const languageNames: Record<PaymentLanguage, string> = {
    ru: 'Русский', en: 'English', kk: 'Қазақша', ky: 'Кыргызча',
};
export const paymentLanguageKey = (currency: string) => `payment.language.${currency.toUpperCase()}`;
export const allowedPaymentLanguages = (currency?: string, requisiteType?: string | null): readonly PaymentLanguage[] => {
    if (requisiteType?.startsWith('rub_transgran_uzs_emission_')) return ['ru', 'en'];
    if (requisiteType === 'qr_vietnam_sber' || requisiteType === 'qr_vietnam_vtb') return ['ru', 'en'];
    switch (currency?.toUpperCase()) {
        case 'KZT': return ['ru', 'en', 'kk'];
        case 'KGS': return ['ru', 'en', 'ky'];
        case 'RUB': return ['en'];
        // Currency is unknown on loading/error screens; do not assume a national language.
        default: return ['ru', 'en'];
    }
};
export function resolvePaymentLanguage(currency: string | undefined, saved: string | null, browserLanguages: readonly string[], requisiteType?: string | null): PaymentLanguage {
    const allowed = allowedPaymentLanguages(currency, requisiteType);
    if (saved && allowed.includes(saved as PaymentLanguage)) return saved as PaymentLanguage;
    for (const tag of browserLanguages) {
        const language = tag.toLowerCase().split(/[-_]/)[0] as PaymentLanguage;
        if (allowed.includes(language)) return language;
    }
    return currency?.toUpperCase() === 'KZT' ? 'kk' : currency?.toUpperCase() === 'KGS' ? 'ky'
        : currency?.toUpperCase() === 'RUB' && !requisiteType?.startsWith('rub_transgran_uzs_emission_') ? 'en' : 'ru';
}
export function paymentErrorMessage(value: unknown, fallback: PaymentMessage): PaymentMessage {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(paymentTranslations, value)
        ? value as PaymentMessage : fallback;
}
export function translatePayment(language: PaymentLanguage, message: PaymentMessage, values: Record<string, string | number> = {}): string {
    const template = language === 'ru' ? message : paymentTranslations[message][language];
    return template.replace(/\{(\w+)\}/g, (placeholder, key) => values[key] === undefined ? placeholder : String(values[key]));
}
const PaymentLanguageContext = createContext({
    language: 'ru' as PaymentLanguage,
    t: (message: PaymentMessage, values?: Record<string, string | number>) => translatePayment('ru', message, values),
    allowed: ['ru', 'en'] as readonly PaymentLanguage[],
    selectLanguage: (_value: string) => {},
});
export const usePaymentLanguage = () => useContext(PaymentLanguageContext);

export const PaymentLanguagePicker = ({ segmented = false }: { segmented?: boolean }) => {
    const { language, t, allowed, selectLanguage } = usePaymentLanguage();
    if (segmented) {
        return <div className="transfer-language-picker" role="group" aria-label={t('Язык')}>
            {allowed.map(item => <button key={item} type="button" lang={item}
                aria-label={languageNames[item]} aria-pressed={language === item}
                onClick={() => selectLanguage(item)}>
                {item.toUpperCase()}
            </button>)}
        </div>;
    }
    if (allowed.length < 2) return null;
    return <div className="flex justify-end bg-gray-50 px-4 py-2 dark:bg-slate-900">
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300">
            {t('Язык')}
            <select aria-label={t('Язык')} value={language} onChange={event => selectLanguage(event.target.value)}
                className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 text-gray-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                {allowed.map(item => <option key={item} value={item} lang={item}>{languageNames[item]}</option>)}
            </select>
        </label>
    </div>;
};

export const PaymentLanguageProvider: React.FC<{ currency?: string; requisiteType?: string | null; showSelector?: boolean; children: React.ReactNode }> = ({ currency, requisiteType, showSelector = true, children }) => {
    const currencyKey = currency?.toUpperCase() || 'unknown';
    const [choices, setChoices] = useState<Record<string, PaymentLanguage>>({});
    let saved: string | null = choices[currencyKey] || null;
    if (!saved) {
        try { saved = window.localStorage.getItem(paymentLanguageKey(currencyKey)); } catch { /* Storage is optional. */ }
    }
    const language = resolvePaymentLanguage(currency, saved, navigator.languages?.length ? navigator.languages : [navigator.language], requisiteType);
    const allowed = allowedPaymentLanguages(currency, requisiteType);
    const t = (message: PaymentMessage, values?: Record<string, string | number>) => translatePayment(language, message, values);
    useEffect(() => {
        const previous = document.documentElement.lang;
        document.documentElement.lang = language;
        return () => { document.documentElement.lang = previous; };
    }, [language]);
    const selectLanguage = (value: string) => {
        if (!allowed.includes(value as PaymentLanguage)) return;
        setChoices(previous => ({ ...previous, [currencyKey]: value as PaymentLanguage }));
        try { window.localStorage.setItem(paymentLanguageKey(currencyKey), value); } catch { /* Keep the in-memory choice. */ }
    };
    return <PaymentLanguageContext.Provider value={{ language, t, allowed, selectLanguage }}>
        {showSelector && <PaymentLanguagePicker />}
        {children}
    </PaymentLanguageContext.Provider>;
};
